import { SubmitHandler, useFormContext, Controller } from "react-hook-form";
import {
  useGetPrioritiesQuery,
  useGetStatusesQuery,
} from "../api/ticketApi/selectInputApis";
import { useGetPaginatedCategoriesQuery } from "@/api/ticketApi/category";
import { useGetSubcategoriesListQuery } from "@/api/ticketApi/subcategory";
import { useGetClientsQuery, useGetClientByIdQuery } from "@/api/clientApi/client";
import { useGetPaginatedUpdateReasonsQuery } from "@/api/ticketApi/updateReason";
import { useGetAgentsQuery, useGetAgentGroupsQuery, useGetAgentByIdQuery } from "@/api/agentApi/agent";
import { useGetGroupsQuery } from "@/api/groupApi/group";
import { useGetAgentsInGroupQuery } from "@/api/groupApi/agentGroup";
import { Button, Label, Modal, Textarea } from "flowbite-react";
import CustomTextInput from "../components/CustomTextInput";
import CustomSelect from "../components/CustomSelect";
import PaginatedSelect from "@/components/PaginatedSelect";
import { useEffect, useMemo, useState } from "react";
import { Ticket, Subcategory } from "@/sharedTypes";
import { useTranslation } from "react-i18next";
import { useCreateCategoryMutation } from "@/api/ticketApi/category";
import { useCreateSubcategoryMutation } from "@/api/ticketApi/subcategory";
import { useCreateClientMutation } from "@/api/clientApi/client";
import { useCreateAgentMutation } from "@/api/agentApi/agent";
import { useCreateUpdateReasonMutation } from "@/api/ticketApi/updateReason";
import CategoryFormDialog from "@/pages/categoryList/CategoryFormDialog";
import SubcategoryFormDialog from "@/pages/subcategories/SubcategoryFormDialog";
import ClientFormDialog from "@/components/clients/ClientFormDialog";
import AgentFormDialog from "@/components/agents/AgentFormDialog";
import UpdateReasonFormDialog from "@/pages/updateReasons/UpdateReasonFormDialog";
import { toast } from "react-toastify";
import { getErrorMessage } from "@/utils/requestUtils";

interface TicketFormDialogProps {
  open: boolean;
  onClose: () => void;
  mode: "create" | "edit";
  onSubmit: (data: Ticket) => void;
  isLoading: boolean;
  editFallbacks?: {
    categoryName?: string;
    subcategoryName?: string;
    clientName?: string;
    agentName?: string;
    updateReasonName?: string;
  };
}

function TicketFormDialog({
  open,
  onClose,
  mode,
  onSubmit,
  isLoading,
  editFallbacks,
}: TicketFormDialogProps) {
  const { t } = useTranslation("ticket");
  const methods = useFormContext<Ticket>();
  const { handleSubmit, watch, setValue, formState, control, clearErrors } =
    methods;

  // Pagination & Search States
  const [categoryPage, setCategoryPage] = useState(1);
  const [categorySearch, setCategorySearch] = useState("");
  const [subcategoryPage, setSubcategoryPage] = useState(1);
  const [subcategorySearch, setSubcategorySearch] = useState("");
  const [clientPage, setClientPage] = useState(1);
  const [clientSearch, setClientSearch] = useState("");
  const [agentPage, setAgentPage] = useState(1);
  const [agentSearch, setAgentSearch] = useState("");
  const [agentGroupPage, setAgentGroupPage] = useState(1);
  const [agentGroupSearch, setAgentGroupSearch] = useState("");
  // Optional group used to filter the agent dropdown (feature: select agent via group)
  const [selectedAgentGroupId, setSelectedAgentGroupId] = useState<number | null>(null);
  const [updateReasonPage, setUpdateReasonPage] = useState(1);
  const [updateReasonSearch, setUpdateReasonSearch] = useState("");

  const { data: priorities } = useGetPrioritiesQuery();
  const { data: statuses } = useGetStatusesQuery();

  const { data: categoriesData, isFetching: isCategoriesFetching } = useGetPaginatedCategoriesQuery({
    pagination: { page: categoryPage, pageSize: 5 },
    filters: { "category_name[like]": categorySearch }
  });

  const selectedCategoryId = watch("last_category_id");
  const { data: subcategoriesData, isFetching: isSubcategoriesFetching } = useGetSubcategoriesListQuery({
    pagination: { page: subcategoryPage, pageSize: 5 },
    filters: { 
        "subcategory_name[like]": subcategorySearch,
        category_id: selectedCategoryId || undefined
    },
  }, { skip: !selectedCategoryId });

  const { data: clientsData, isFetching: isClientsFetching } = useGetClientsQuery({
    page: clientPage,
    pageSize: 5,
    client_username: clientSearch
  });

  // Agent groups list (options for the optional group filter)
  const { data: agentGroupsData, isFetching: isAgentGroupsFetching } = useGetGroupsQuery({
    pagination: { page: agentGroupPage, pageSize: 5 },
    filters: { group_name: agentGroupSearch || undefined },
  });

  // Agents: either the full list, or agents restricted to the selected group.
  const isGroupFiltered = !!selectedAgentGroupId;

  const { data: allAgentsData, isFetching: isAllAgentsFetching } = useGetAgentsQuery(
    {
      page: agentPage,
      pageSize: 5,
      agent_username: agentSearch,
    },
    { skip: isGroupFiltered }
  );

  const { data: groupAgentsData, isFetching: isGroupAgentsFetching } =
    useGetAgentsInGroupQuery(
      {
        groupId: String(selectedAgentGroupId),
        params: {
          pagination: { page: agentPage, pageSize: 5 },
          filters: { agent_username: agentSearch || undefined },
        },
      },
      { skip: !isGroupFiltered }
    );

  const agentsData = isGroupFiltered ? groupAgentsData : allAgentsData;
  const isAgentsFetching = isGroupFiltered ? isGroupAgentsFetching : isAllAgentsFetching;

  const { data: updateReasonsData, isFetching: isUpdateReasonsFetching } = useGetPaginatedUpdateReasonsQuery({
    pagination: { page: updateReasonPage, pageSize: 5 },
    filters: { 
        description: updateReasonSearch,
        is_active: true
    }
  });

  // Derived Options
  const categories = categoriesData?.items || [];
  const subcategories = subcategoriesData?.items || [];
  const clients = clientsData?.items || [];
  const agents = agentsData?.items || [];
  const updateReasons = updateReasonsData?.items || [];

  const [createCategory] = useCreateCategoryMutation();
  const [createSubcategory] = useCreateSubcategoryMutation();
  const [createClient] = useCreateClientMutation();
  const [createAgent] = useCreateAgentMutation();
  const [createUpdateReason] = useCreateUpdateReasonMutation();

  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isSubcategoryModalOpen, setIsSubcategoryModalOpen] = useState(false);
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [isAgentModalOpen, setIsAgentModalOpen] = useState(false);
  const [isUpdateReasonModalOpen, setIsUpdateReasonModalOpen] = useState(false);

  const [assignTo, setAssignTo] = useState<"self" | "agent">("self");
  const lastAssignedAgentId = watch("last_assigned_agent_id");

  // The agents list is paginated (200+ entries) and the dropdown only loads
  // one page at a time. When editing, the ticket's assigned agent may live on
  // a page that isn't loaded, so it wouldn't appear as selected. Fetch that
  // agent directly by id and merge it into the options so the current
  // selection always resolves and shows up highlighted in the dropdown.
  const isSelectedAgentLoaded = agents.some(
    (a: any) => String(a.agent_id) === String(lastAssignedAgentId),
  );
  const { data: selectedAgentById } = useGetAgentByIdQuery(
    String(lastAssignedAgentId),
    { skip: !lastAssignedAgentId || isSelectedAgentLoaded },
  );

  const assignedAgentLabel =
    selectedAgentById?.agent_username || editFallbacks?.agentName;

  const agentOptions = useMemo(() => {
    const opts = agents.map((a: any) => ({
      value: a.agent_id,
      label: a.agent_username,
    }));
    if (
      lastAssignedAgentId &&
      !opts.some((o: any) => String(o.value) === String(lastAssignedAgentId)) &&
      assignedAgentLabel
    ) {
      opts.unshift({ value: lastAssignedAgentId, label: assignedAgentLabel });
    }
    return opts;
  }, [agents, lastAssignedAgentId, assignedAgentLabel]);

  // The clients list is paginated the same way (180+ entries), so the ticket's
  // client may not be on the loaded page. Fetch it by id and merge it into the
  // options so the current selection always resolves. Note: client_displayname
  // is frequently null, so the label falls back to client_username.
  const clientIdValue = watch("client_id");
  const isSelectedClientLoaded = clients.some(
    (c: any) => String(c.client_id) === String(clientIdValue),
  );
  const { data: selectedClientById } = useGetClientByIdQuery(
    String(clientIdValue),
    { skip: !clientIdValue || isSelectedClientLoaded },
  );
  const selectedClientLabel =
    selectedClientById?.client_displayname ||
    selectedClientById?.client_username ||
    editFallbacks?.clientName;

  const clientOptions = useMemo(() => {
    const opts = clients.map((c: any) => ({
      value: c.client_id,
      label: c.client_displayname || c.client_username,
    }));
    if (
      clientIdValue &&
      !opts.some((o: any) => String(o.value) === String(clientIdValue)) &&
      selectedClientLabel
    ) {
      opts.unshift({ value: clientIdValue, label: selectedClientLabel });
    }
    return opts;
  }, [clients, clientIdValue, selectedClientLabel]);

  // Groups the selected agent belongs to (feature: show group after selecting an agent)
  const { data: selectedAgentGroupsData, isFetching: isSelectedAgentGroupsFetching } =
    useGetAgentGroupsQuery(
      {
        agentId: String(lastAssignedAgentId),
        params: { pagination: { page: 1, pageSize: 50 }, filters: {} },
      },
      { skip: !lastAssignedAgentId || assignTo !== "agent" }
    );
  const selectedAgentGroups = selectedAgentGroupsData?.items || [];

  // Handlers for creating entities
  const handleCategoryCreate = async (data: any) => {
    try {
      const res = await createCategory(data).unwrap();
      toast.success("Category created successfully");
      setIsCategoryModalOpen(false);
      const newId = (res as any).category_id || (res as any).data?.category_id;
      if (newId) {
        setValue("last_category_id", newId);
        setValue("last_subcategory_id", null);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleSubcategoryCreate = async (data: any) => {
    try {
      const res = await createSubcategory(data).unwrap();
      toast.success("Subcategory created successfully");
      setIsSubcategoryModalOpen(false);
      if (data.category_id) {
        setValue("last_category_id", Number(data.category_id));
      }
      const newId = (res as any).subcategory_id || (res as any).data?.subcategory_id;
      if (newId) setValue("last_subcategory_id", newId);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleClientCreate = async (data: any) => {
    try {
      const res = await createClient(data).unwrap();
      toast.success("User created successfully");
      setIsClientModalOpen(false);
      const newId = (res as any).client_id || (res as any).data?.client_id;
      if (newId) setValue("client_id", Number(newId));
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleAgentCreate = async (data: any) => {
    try {
      const res = await createAgent(data).unwrap();
      toast.success("Agent created successfully");
      setIsAgentModalOpen(false);
      // const newId = res.agent_id; // Check response structure if needed
      // Sometimes just res.data...
      const newId = res?.agent_id || (res as any)?.data?.agent_id;
      if(newId) setValue("last_assigned_agent_id", newId);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleUpdateReasonCreate = async (data: any) => {
    try {
      const res = await createUpdateReason(data).unwrap();
      toast.success("Update reason created successfully");
      setIsUpdateReasonModalOpen(false);
      const newId = res?.update_reason_id || res?.data?.update_reason_id;
      if(newId) setValue("update_reason_id", newId);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  // Helper to convert ID to Select Option. Falls back to a known label
  // (e.g. from the edited ticket) when the item is not in the loaded page.
  const getOption = (
    id: number | string | undefined | null,
    list: any[],
    idKey: string,
    labelKey: string,
    fallbackLabel?: string,
  ) => {
    if (!id) return null;
    const item = list.find((i) => i[idKey]?.toString() === id.toString());
    if (item) return { value: item[idKey], label: item[labelKey] };
    if (fallbackLabel) return { value: id, label: fallbackLabel };
    return null;
  };

  // Logic to set "Assign To" based on current agent
  useEffect(() => {
    if (mode === "edit") {
      const savedUserData = JSON.parse(localStorage.getItem("userData") || "{}");
      const loggedInAgentId = savedUserData?.agent_id;

      if (lastAssignedAgentId && lastAssignedAgentId !== loggedInAgentId) {
        setAssignTo("agent");
      } else {
        setAssignTo("self");
      }
    }
  }, [mode, lastAssignedAgentId]);

  // ----------------------------
  // Resolution note + status change guard
  // ----------------------------
  const resolvedStatusId = useMemo(() => {
    const list = statuses?.items ?? [];
    const match = list.find((s: any) => {
      const n = String(s?.status_name ?? "").toLowerCase();
      return n.includes("resolved") || n.includes("çözü") || n.includes("cozu");
    });
    return match?.status_id ? Number(match.status_id) : null;
  }, [statuses]);

  const watchedStatusId = watch("last_status_id");

  const [formInitialized, setFormInitialized] = useState(false);
  const [initialStatusId, setInitialStatusId] = useState<number | null>(null);

  const [showStatusChangeWarning, setShowStatusChangeWarning] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<number | null>(
    null
  );

  useEffect(() => {
    if (mode === "edit" && open && !formInitialized) {
      const currentStatus = methods.getValues("last_status_id");
      setInitialStatusId(
        currentStatus === undefined || currentStatus === null
          ? null
          : Number(currentStatus)
      );
      setFormInitialized(true);
    } else if (!open) {
      setFormInitialized(false);
      setInitialStatusId(null);
      setShowStatusChangeWarning(false);
      setPendingStatusChange(null);
    }
  }, [mode, open, formInitialized, methods]);

  const handleStatusChange = (newStatusId: number) => {
    // Resolved ID bilinmiyorsa, sadece set et (warning/note mantığını zorlamayalım)
    if (!resolvedStatusId) {
      setValue("last_status_id", newStatusId);
      return;
    }

    const currentStatus = Number(methods.getValues("last_status_id") ?? 0);
    const wasResolved = currentStatus === resolvedStatusId;
    const isChangingFromResolved = wasResolved && newStatusId !== resolvedStatusId;

    if (isChangingFromResolved) {
      setPendingStatusChange(newStatusId);
      setShowStatusChangeWarning(true);
      return;
    }

    setValue("last_status_id", newStatusId);

    // Resolved değilse resolution_note'u temizle (opsiyonel ama temiz davranış)
    if (newStatusId !== resolvedStatusId) {
      setValue("resolution_note" as any, null as any);
      clearErrors("resolution_note" as any);
    }
  };

  const confirmStatusChange = () => {
    if (pendingStatusChange !== null) {
      setValue("last_status_id", pendingStatusChange);
      if (resolvedStatusId && pendingStatusChange !== resolvedStatusId) {
        setValue("resolution_note" as any, null as any);
        clearErrors("resolution_note" as any);
      }
    }
    setShowStatusChangeWarning(false);
    setPendingStatusChange(null);
  };

  const cancelStatusChange = () => {
    setShowStatusChangeWarning(false);
    setPendingStatusChange(null);
  };

  const handleFormSubmit: SubmitHandler<Ticket> = async (data) => {
    const savedUserData = JSON.parse(localStorage.getItem("userData") || "{}");
    const agentId = savedUserData?.agent_id || 0;

    // Eğer resolved değilse resolution_note'u göndermeyelim (backend strict ise)
    const shouldSendResolutionNote =
      resolvedStatusId &&
      Number(data?.last_status_id ?? 0) === resolvedStatusId &&
      initialStatusId !== resolvedStatusId;

    const payload: Ticket = {
      ...data,
      last_category_id: parseInt(data?.last_category_id?.toString() || ""),
      last_assigned_agent_id:
        assignTo === "self" ? agentId : data.last_assigned_agent_id,
      ...(shouldSendResolutionNote ? {} : { resolution_note: undefined as any }),
    };

    onSubmit(payload);
  };

  return (
    <>
      <Modal show={open} onClose={onClose}>
        <Modal.Header>
          {mode === "create" ? t("ticketForm.createTitle") : t("ticketForm.editTitle")}
        </Modal.Header>
        <Modal.Body>
          <div className="max-h-[80vh] overflow-y-auto">
            <form onSubmit={handleSubmit(handleFormSubmit)}>
              {mode === "edit" && (
                <Controller
                  name="last_status_id"
                  control={control}
                  rules={{ required: t("ticketForm.statusRequired") }}
                  render={({ field }) => (
                    <CustomSelect
                      id="status_id"
                      label={t("ticketForm.status")}
                      options={statuses?.items?.map((status: any) => ({
                        value: status.status_id?.toString(),
                        label: status.status_name,
                      }))}
                      value={field.value?.toString() ?? ""}
                      onChange={(e: any) => {
                        const v = Number(e?.target?.value);
                        if (!Number.isNaN(v)) {
                          handleStatusChange(v);
                          clearErrors("last_status_id");
                        }
                      }}
                      error={formState.errors?.last_status_id?.message}
                    />
                  )}
                />
              )}

              {/* Resolution Note (sadece resolved'a geçişte) */}
              {mode === "edit" &&
                formInitialized &&
                resolvedStatusId &&
                Number(watchedStatusId ?? 0) === resolvedStatusId &&
                initialStatusId !== resolvedStatusId && (
                  <div className="mb-4">
                    <Label htmlFor="resolution_note">{t("ticketForm.resolutionNote")}</Label>
                    <Controller
                      name={"resolution_note" as any}
                      control={control}
                      rules={{
                        required: t("ticketForm.resolutionRequired"),
                      }}
                      render={({ field }) => (
                        <Textarea
                          id="resolution_note"
                          placeholder={t("ticketForm.resolutionPlaceholder")}
                          rows={4}
                          value={(field.value as any) ?? ""}
                          onChange={(e: any) => field.onChange(e.target.value)}
                        />
                      )}
                    />
                    {formState.errors?.resolution_note?.message && (
                      <p className="mt-1 text-sm text-red-600">
                        {String(formState.errors.resolution_note.message)}
                      </p>
                    )}
                  </div>
                )}

              <CustomTextInput
                id="ticket_title"
                label={t("ticketForm.title")}
                placeholder={t("ticketForm.titlePlaceholder")}
                {...methods.register("ticket_title", {
                  required: t("ticketForm.titleRequired"),
                })}
                error={formState.errors.ticket_title?.message}
              />

              <CustomTextInput
                id="ticket_description"
                label={t("ticketForm.description")}
                placeholder={t("ticketForm.descriptionPlaceholder")}
                {...methods.register("ticket_description", {
                  required: t("ticketForm.descriptionRequired"),
                })}
                error={formState.errors.ticket_description?.message}
              />

              <CustomSelect
                id="last_priority_id"
                label={t("ticketForm.priority")}
                options={priorities?.items?.map((priority: any) => ({
                  value: priority.priority_id?.toString(),
                  label: priority?.priority_name,
                }))}
                {...methods.register("last_priority_id", {
                  required: t("ticketForm.priorityRequired"),
                  valueAsNumber: true,
                })}
                error={formState.errors?.last_priority_id?.message}
              />

              <div className="mb-4">
                <Controller
                  name="last_category_id"
                  control={control}
                  rules={{ required: t("ticketForm.categoryRequired") }}
                  render={({ field }) => (
                    <PaginatedSelect
                      id="last_category_id"
                      label={t("ticketForm.category")}
                      value={getOption(field.value, categories, "category_id", "category_name", editFallbacks?.categoryName)}
                      onChange={(option: any) => {
                        field.onChange(option?.value ? Number(option.value) : null);
                        setValue("last_subcategory_id", null);
                      }}
                      options={categories.map((c: any) => ({
                        value: c.category_id,
                        label: c.category_name
                      }))}
                      onInputChange={(val) => { setCategorySearch(val); setCategoryPage(1); }}
                      page={categoryPage}
                      totalPages={categoriesData?.pagination?.totalPages || 1}
                      onPageChange={setCategoryPage}
                      isLoading={isCategoriesFetching}
                      onAddClick={() => setIsCategoryModalOpen(true)}
                      placeholder={t("ticketForm.selectCategory")}
                    />
                  )}
                />
                {formState.errors?.last_category_id?.message && (
                  <p className="mt-1 text-sm text-red-500">{formState.errors.last_category_id.message}</p>
                )}
              </div>

              <div className="mb-4">
                <Controller
                  name="last_subcategory_id"
                  control={control}
                  render={({ field }) => (
                    <PaginatedSelect
                      id="last_subcategory_id"
                      label={t("ticketForm.subcategory")}
                      value={getOption(field.value, subcategories, "subcategory_id", "subcategory_name", editFallbacks?.subcategoryName)}
                      onChange={(option: any) => field.onChange(option?.value ? Number(option.value) : null)}
                      options={
                         subcategories.map((s: any) => ({
                          value: s.subcategory_id,
                          label: s.subcategory_name
                        }))
                      }
                      onInputChange={(val) => { setSubcategorySearch(val); setSubcategoryPage(1); }}
                      page={subcategoryPage}
                      totalPages={subcategoriesData?.pagination?.totalPages || 1}
                      onPageChange={setSubcategoryPage}
                      isLoading={isSubcategoriesFetching}
                      onAddClick={() => setIsSubcategoryModalOpen(true)}
                      placeholder={t("ticketForm.selectSubcategory")}
                    />
                  )}
                />
                 {formState.errors?.last_subcategory_id?.message && (
                  <p className="mt-1 text-sm text-red-500">{formState.errors.last_subcategory_id.message}</p>
                )}
              </div>

              <div className="mb-4">
                <Controller
                  name="client_id"
                  control={control}
                  rules={{ required: t("ticketForm.clientRequired") }}
                  render={({ field }) => (
                    <PaginatedSelect
                      id="client_id"
                      label={t("ticketForm.client")}
                      value={clientOptions.find((o: any) => String(o.value) === String(field.value)) || null}
                      onChange={(option: any) => field.onChange(option?.value ? Number(option.value) : null)}
                      options={clientOptions}
                      onInputChange={(val) => { setClientSearch(val); setClientPage(1); }}
                      page={clientPage}
                      totalPages={clientsData?.pagination?.totalPages || 1}
                      onPageChange={setClientPage}
                      isLoading={isClientsFetching}
                      onAddClick={() => setIsClientModalOpen(true)}
                      placeholder={t("ticketForm.clientPlaceholder")}
                    />
                  )}
                />
                 {formState.errors?.client_id?.message && (
                  <p className="mt-1 text-sm text-red-500">{formState.errors.client_id.message}</p>
                )}
              </div>

              {mode === "edit" && (
                <div className="mb-4">
                  <Controller
                    name="update_reason_id"
                    control={control}
                    rules={{ required: t("ticketForm.updateReasonRequired") }}
                    render={({ field }) => (
                      <PaginatedSelect
                        id="update_reason_id"
                        label={t("ticketForm.updateReason")}
                        value={getOption(field.value, updateReasons, "update_reason_id", "update_reason_description", editFallbacks?.updateReasonName)}
                        onChange={(option: any) => field.onChange(option?.value ? Number(option.value) : null)}
                        options={updateReasons.map((u: any) => ({
                          value: u.update_reason_id,
                          label: u.update_reason_description
                        }))}
                        onInputChange={(val) => { setUpdateReasonSearch(val); setUpdateReasonPage(1); }}
                        page={updateReasonPage}
                        totalPages={updateReasonsData?.pagination?.totalPages || 1}
                        onPageChange={setUpdateReasonPage}
                        isLoading={isUpdateReasonsFetching}
                        onAddClick={() => setIsUpdateReasonModalOpen(true)}
                        placeholder={t("ticketForm.selectUpdateReason")}
                      />
                    )}
                  />
                  {formState.errors?.update_reason_id?.message && (
                    <p className="mt-1 text-sm text-red-500">{formState.errors.update_reason_id.message}</p>
                  )}
                </div>
              )}

              <div className="mb-4">
                <Label htmlFor="assign_to">{t("ticketForm.assignTo")}</Label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="self"
                      checked={assignTo === "self"}
                      onChange={() => setAssignTo("self")}
                      className="mr-2"
                    />
                    <p className="text-gray-900 dark:text-gray-300">{t("ticketForm.assignToSelf")}</p>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="agent"
                      checked={assignTo === "agent"}
                      onChange={() => setAssignTo("agent")}
                      className="mr-2"
                    />
                    <p className="text-gray-900 dark:text-gray-300">{t("ticketForm.assignToAgent")}</p>
                  </label>
                </div>
              </div>

              {assignTo === "agent" && (
                <>
                  {/* Optional: filter agents by group */}
                  <div className="mb-4">
                    <PaginatedSelect
                      id="agent_group_filter"
                      label={t("ticketForm.agentGroup")}
                      value={getOption(
                        selectedAgentGroupId,
                        agentGroupsData?.items || [],
                        "group_id",
                        "group_name",
                      )}
                      onChange={(option: any) => {
                        setSelectedAgentGroupId(option?.value ? Number(option.value) : null);
                        // Reset agent list & clear any selection outside the new group
                        setAgentPage(1);
                        setAgentSearch("");
                        setValue("last_assigned_agent_id", null as any);
                      }}
                      options={(agentGroupsData?.items || []).map((g: any) => ({
                        value: g.group_id,
                        label: g.group_name,
                      }))}
                      onInputChange={(val) => { setAgentGroupSearch(val); setAgentGroupPage(1); }}
                      page={agentGroupPage}
                      totalPages={agentGroupsData?.pagination?.totalPages || 1}
                      onPageChange={setAgentGroupPage}
                      isLoading={isAgentGroupsFetching}
                      placeholder={t("ticketForm.selectAgentGroup")}
                    />
                  </div>

                  <div className="mb-4">
                     <Controller
                      name="last_assigned_agent_id"
                      control={control}
                      rules={{ required: t("ticketForm.agentRequired") }}
                      render={({ field }) => (
                        <PaginatedSelect
                          id="last_assigned_agent_id"
                          label={t("ticketForm.agent")}
                          value={getOption(field.value, agents, "agent_id", "agent_username", assignedAgentLabel)}
                          onChange={(option: any) => field.onChange(option?.value ? Number(option.value) : null)}
                          options={agentOptions}
                          onInputChange={(val) => { setAgentSearch(val); setAgentPage(1); }}
                          page={agentPage}
                          totalPages={agentsData?.pagination?.totalPages || 1}
                          onPageChange={setAgentPage}
                          isLoading={isAgentsFetching}
                          onAddClick={() => setIsAgentModalOpen(true)}
                          placeholder={t("ticketForm.selectAgent")}
                        />
                      )}
                    />
                    {formState.errors?.last_assigned_agent_id?.message && (
                      <p className="mt-1 text-sm text-red-500">{formState.errors.last_assigned_agent_id.message}</p>
                    )}

                    {/* Show the groups the selected agent belongs to */}
                    {lastAssignedAgentId && (
                      <div className="mt-2">
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                          {t("ticketForm.agentGroupsLabel")}:
                        </span>
                        {isSelectedAgentGroupsFetching ? (
                          <span className="ml-2 text-xs text-gray-400">…</span>
                        ) : selectedAgentGroups.length > 0 ? (
                          <div className="mt-1 flex flex-wrap gap-1.5">
                            {selectedAgentGroups.map((g: any) => (
                              <span
                                key={g.group_id}
                                className="inline-flex items-center rounded-lg bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900 dark:text-blue-300"
                              >
                                {g.group_name}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="ml-2 text-xs text-gray-400">
                            {t("ticketForm.agentNoGroup")}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading
                  ? mode === "create"
                    ? t("ticketForm.creating")
                    : t("ticketForm.updating")
                  : mode === "create"
                    ? t("ticketForm.createButton")
                    : t("ticketForm.updateButton")}
              </Button>
            </form>
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={showStatusChangeWarning} onClose={cancelStatusChange}>
        <Modal.Header>{t("ticketForm.statusChangeWarningTitle")}</Modal.Header>
        <Modal.Body>
          <p className="text-sm text-gray-700">
            {t("ticketForm.statusChangeWarningBody")}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button color="gray" onClick={cancelStatusChange}>
            {t("ticketForm.cancel")}
          </Button>
          <Button onClick={confirmStatusChange}>{t("ticketForm.continue")}</Button>
        </Modal.Footer>
      </Modal>

      {/* Creation Modals */}
      <CategoryFormDialog
        open={isCategoryModalOpen}
        onClose={() => setIsCategoryModalOpen(false)}
        onSubmit={handleCategoryCreate}
      />
      
      <SubcategoryFormDialog
        open={isSubcategoryModalOpen}
        onClose={() => setIsSubcategoryModalOpen(false)}
        onSubmit={handleSubcategoryCreate}
        initialData={selectedCategoryId ? ({ category_id: selectedCategoryId } as Subcategory) : undefined}
      />

      <ClientFormDialog
        open={isClientModalOpen}
        onClose={() => setIsClientModalOpen(false)}
        onSubmit={handleClientCreate}
      />
      
      <AgentFormDialog
        open={isAgentModalOpen}
        onClose={() => setIsAgentModalOpen(false)}
        onSubmit={handleAgentCreate}
      />

      <UpdateReasonFormDialog
        open={isUpdateReasonModalOpen}
        onClose={() => setIsUpdateReasonModalOpen(false)}
        onSubmit={handleUpdateReasonCreate}
      />
    </>
  );
}

export default TicketFormDialog;
